import { useEffect, useRef, useState } from "react";
import { unlockPlayback, releasePlayback } from "./unlockAudio";

const API = process.env.REACT_APP_BACKEND_URL;
export const LAYER_KEYS = ["music", "soundscape", "spoken", "softer", "whisper", "voice"];

// Six real stems, started together and looped; each layer is faded in or out on its own gain.
export const useLayerMix = () => {
  const ctx = useRef(null);
  const nodes = useRef({});
  const raf = useRef(0);
  const [state, setState] = useState("idle");
  const [on, setOn] = useState({});
  const [levels, setLevels] = useState({});

  useEffect(() => () => { cancelAnimationFrame(raf.current); ctx.current?.close(); releasePlayback(); }, []);

  const meter = () => {
    const buf = new Uint8Array(256);
    const tick = () => {
      const out = {};
      LAYER_KEYS.forEach((k) => {
        const a = nodes.current[k]?.analyser;
        if (!a) return;
        a.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) { const v = (buf[i] - 128) / 128; sum += v * v; }
        out[k] = Math.min(1, Math.sqrt(sum / buf.length) * 14);
      });
      setLevels(out);
      raf.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const set = (k, value, secs = 0.8) => {
    const g = nodes.current[k]?.gain.gain;
    if (!g) return;
    const t = ctx.current.currentTime;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(value, t + secs);
    setOn((o) => ({ ...o, [k]: value > 0.05 }));
  };

  const start = async (initial = ["music"]) => {
    if (state === "ready") return true;
    setState("loading");
    unlockPlayback();
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const c = new AC();
      ctx.current = c;
      await c.resume();
      const bufs = await Promise.all(LAYER_KEYS.map((k) => fetch(`${API}/api/soulumo-launch/layer/${k}.mp3`).then((r) => {
        if (!r.ok) throw new Error(k);
        return r.arrayBuffer();
      }).then((b) => new Promise((res, rej) => c.decodeAudioData(b, res, rej)))));
      const when = c.currentTime + 0.15;
      LAYER_KEYS.forEach((k, i) => {
        const src = c.createBufferSource();
        src.buffer = bufs[i];
        src.loop = true;
        const gain = c.createGain();
        gain.gain.value = initial.includes(k) ? 1 : 0;
        const analyser = c.createAnalyser();
        analyser.fftSize = 256;
        src.connect(gain).connect(analyser).connect(c.destination);
        src.start(when);
        nodes.current[k] = { src, gain, analyser };
      });
      setOn(Object.fromEntries(LAYER_KEYS.map((k) => [k, initial.includes(k)])));
      setState("ready");
      meter();
      return true;
    } catch {
      setState("error");
      return false;
    }
  };

  const pause = () => { ctx.current?.suspend(); releasePlayback(); setState("paused"); };
  const resume = () => { unlockPlayback(); ctx.current?.resume(); setState("ready"); };
  return { state, on, levels, start, set, pause, resume };
};
